import { format } from "date-fns";
import axios from "axios";
import { Loader2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useFoodVersions } from "@/hooks/useFoodVersions";
import type { FoodVersionChangedPart } from "@/lib/food-version-api";

/**
 * Read-only version history for one food (ZCS-463, contract §4).
 *
 * Read-only on purpose. The publish and discard controls belong with the rest
 * of the draft surface, which waits for FV6 (ZCS-464) so it is never built
 * across the split where nutrients stage and servings write through instantly.
 */

const CHANGED_LABELS: Record<FoodVersionChangedPart, string> = {
  profile: "Profile",
  nutrient_values: "Nutrient values",
  servings: "Servings",
  tags: "Tags",
};

const changedLabel = (part: FoodVersionChangedPart) =>
  CHANGED_LABELS[part] ?? part;

interface FoodVersionHistoryProps {
  foodItemId: number;
}

export const FoodVersionHistory = ({ foodItemId }: FoodVersionHistoryProps) => {
  const { data, isLoading, error } = useFoodVersions(foodItemId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground/70" />
      </div>
    );
  }

  if (error) {
    // Three failures worth telling apart, because they mean different things to
    // whoever is looking at the screen.
    const status = axios.isAxiosError(error) ? error.response?.status : null;
    const message =
      status === 403
        ? "Version history is only visible to clinical console users."
        : status === 404
          ? "Version history isn't available for this food yet."
          : "Version history couldn't be loaded.";
    return (
      <p className="z-caption text-muted-foreground py-8 text-center">
        {message}
      </p>
    );
  }

  const versions = data?.results ?? [];

  if (versions.length === 0) {
    return (
      <p className="z-caption text-muted-foreground py-8 text-center">
        No versions recorded yet.
      </p>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      <div className="bg-muted/50 border-b border-border px-3 py-2">
        <h4 className="z-label text-foreground">Version history</h4>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-border/60 hover:bg-transparent">
              <TableHead className="z-eyebrow text-muted-foreground h-8 px-3">
                Version
              </TableHead>
              <TableHead className="z-eyebrow text-muted-foreground h-8 px-3">
                Changed
              </TableHead>
              <TableHead className="z-eyebrow text-muted-foreground h-8 px-3">
                Updated by
              </TableHead>
              <TableHead className="z-eyebrow text-muted-foreground h-8 px-3">
                When
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {versions.map((version) => (
              <TableRow
                key={version.version_number}
                className="border-border/60 hover:bg-muted/20"
              >
                <TableCell className="px-3 py-2 z-caption text-foreground tabular-nums">
                  <span className="inline-flex items-center gap-2">
                    v{version.version_number}
                    {version.is_current && (
                      <Badge variant="success">Current</Badge>
                    )}
                  </span>
                </TableCell>
                <TableCell className="px-3 py-2 z-caption text-muted-foreground">
                  {/* Coarse by design — which parts of the profile moved, not a
                      field-level diff (contract §9 row 4). */}
                  {version.changed.length > 0
                    ? version.changed.map(changedLabel).join(", ")
                    : "—"}
                </TableCell>
                <TableCell className="px-3 py-2 z-caption text-muted-foreground">
                  {/* Null wherever no human acted — a bulk import, the backfill,
                      an entry re-pinning itself, or any version minted before
                      this was recorded. No author is invented for those. */}
                  {version.created_by ?? "Not recorded"}
                </TableCell>
                <TableCell className="px-3 py-2 z-caption text-muted-foreground whitespace-nowrap">
                  {format(new Date(version.created_at), "d MMM yyyy, h:mm a")}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
